import { BreakoutsFilledIcon, Button, Flex, Text } from "@minervaproject/fds";
import { useDrag } from "react-dnd";

import { FlightErrorBoundary } from "components/error_boundary";
import { ResourceButton } from "components/sidebar/resources/resource_button";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import { featureBreakoutRoomResource } from "shared/reducers/classroom";
import { BreakoutRoomType, BreakoutType, PaneContentType } from "shared/types";
import { classroomSelectors } from "store";

function BreakoutResourceButton({ breakoutRoom }: { breakoutRoom: BreakoutRoomType }) {
  const dispatch = useAppDispatch();
  const resources = useArraySelector(
    classroomSelectors.selectResourcesByIds(breakoutRoom.resource_ids),
  );
  const breakoutNotesResource = resources.find((resource) => resource.is_breakout_group_notes);

  const [{ isDragging }, drag] = useDrag({
    type: PaneContentType.Resource,
    item: { id: breakoutNotesResource?.id },
    collect,
  });
  useDragHandler(isDragging);

  const handleFeatureResource = () => {
    dispatch(featureBreakoutRoomResource({ breakoutRoomId: breakoutRoom.id }));
  };

  return (
    <Button
      className="mb1 flex-none"
      kind="classroom-secondary"
      onClick={handleFeatureResource}
      ref={drag}
    >
      Group {breakoutRoom.title}
    </Button>
  );
}

function BreakoutResourceDetails({
  breakout,
  hideCallback,
}: {
  breakout: BreakoutType;
  hideCallback: () => void;
}) {
  const breakoutRooms = useArraySelector(
    classroomSelectors.selectBreakoutRoomsByBreakoutId(breakout.id),
  );

  return (
    <div className="flex flex-column flex-none">
      <Text className="body-l flex-none" kind="h2" mb={4}>
        {breakout.title}
      </Text>
      {breakoutRooms.map((breakoutRoom) => (
        <FlightErrorBoundary
          key={breakoutRoom.id}
          text={`Breakout resource for group ${breakoutRoom.title} cannot be displayed`}
          margin="0 8px 8px"
        >
          <BreakoutResourceButton breakoutRoom={breakoutRoom} />
        </FlightErrorBoundary>
      ))}
      <Button
        kind="text-classroom-secondary"
        className="justify-center"
        onClick={hideCallback}
        mt={5}
      >
        Back
      </Button>
    </div>
  );
}

function InstructorBreakoutResources({
  handleSelectBreakout,
}: {
  handleSelectBreakout: (breakout: BreakoutType) => void;
}) {
  const breakouts = Object.values(useAppSelector(classroomSelectors.selectBreakouts()));

  if (breakouts.length === 0) return null;

  return (
    <Flex direction="column" none className="mb5">
      <Text className="body-l flex-none" kind="h2" mb={4}>
        Breakout Resources
      </Text>
      {breakouts.map((breakout) => (
        <FlightErrorBoundary key={breakout.id} text="Breakout resources cannot be displayed">
          <Button
            className="mb1 flex-none truncate"
            kind="classroom-secondary"
            onClick={() => handleSelectBreakout(breakout)}
          >
            <BreakoutsFilledIcon color="white" />
            <span className="truncate">{breakout.title}</span>
          </Button>
        </FlightErrorBoundary>
      ))}
    </Flex>
  );
}

function StudentBreakoutResources() {
  const studentBreakoutNotes = useArraySelector(
    classroomSelectors.selectBreakoutNotesForCurrentUser(),
  );

  if (studentBreakoutNotes.length === 0) return null;

  return (
    <Flex direction="column" none className="mb5">
      <Text className="body-l flex-none" kind="h2" mb={4}>
        Breakout Resources
      </Text>
      {studentBreakoutNotes.map((resource) => (
        <FlightErrorBoundary
          key={resource.id}
          text="Resource cannot be displayed"
          margin="0 8px 8px"
        >
          <ResourceButton resource={resource} />
        </FlightErrorBoundary>
      ))}
    </Flex>
  );
}

function VisitingBreakoutResources() {
  const visitingBreakoutRoom = useAppSelector(
    classroomSelectors.selectVisitingBreakoutRoomForCurrentUser(),
  );
  const resources = useArraySelector(
    classroomSelectors.selectResourcesByIds(visitingBreakoutRoom?.resource_ids || []),
  );

  const visitingBreakoutRoomNotes = visitingBreakoutRoom
    ? resources.filter(
        (resource) =>
          visitingBreakoutRoom.resource_ids.includes(resource.id) &&
          resource.is_breakout_group_notes,
      )
    : [];

  if (visitingBreakoutRoomNotes.length === 0) return null;

  return (
    <Flex direction="column" none className="mb5">
      <Text className="body-l flex-none" kind="h2" mb={4}>
        Breakout Resources
      </Text>
      {visitingBreakoutRoomNotes.map((resource) => (
        <FlightErrorBoundary
          key={resource.id}
          text="Resource cannot be displayed"
          margin="0 8px 8px"
        >
          <ResourceButton resource={resource} />
        </FlightErrorBoundary>
      ))}
    </Flex>
  );
}

export {
  BreakoutResourceButton,
  BreakoutResourceDetails,
  InstructorBreakoutResources,
  StudentBreakoutResources,
  VisitingBreakoutResources,
};
